import React, { useState, useEffect } from "react";
import { Navbar, Nav, Container, Button, NavDropdown } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import HeroSection from "./HeroSection";
import WhyLibertusSection from "./WhyLibertusSection";
import ArchitectureSection from "./ArchitectureSection";
import FullWidthCTACard from "./FullWidthCTACard";
import IndustrySolutionsSection from "./IndustrySolutionsSection";
import HowItWorksSection from "./HowItWorksSection";
import NewsCarouselSection from "./NewsCarouselSection";
import ModelSelectionModal from "./ModelSelectionModal"; // Import the new modal component
import PlatformCapabilitiesSection from "./PlatformCapabilitiesSection"; // Import the new section
import ChatbotModal from "./ChatbotModal"; // Import the ChatbotModal component
import ContactUsModal from "./ContactUsModal"; // Import the new ContactUsModal component
import { Toast, ToastContainer } from "react-bootstrap"; // Import Toast and ToastContainer

const App = () => {
  const [showModelModal, setShowModelModal] = useState(false);
  const [modelModalContent, setModelModalContent] = useState({
    heading: "",
    models: [],
    agents: [],
  });

  const [showContactModal, setShowContactModal] = useState(false); // State for ContactUsModal
  const [showToast, setShowToast] = useState(false); // State for Toast notification
  const [toastMessage, setToastMessage] = useState(""); // State for Toast message

  const handleShowContactModal = () => setShowContactModal(true);
  const handleCloseContactModal = () => setShowContactModal(false);

  const handleShowToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };
  const handleCloseToast = () => setShowToast(false);

  const modelsList = [
    {
      name: "Llama 3.1 405B",
      description: "Meta's most powerful open-source model, matching GPT-4 capabilities.",
      link: "https://ai.meta.com/blog/llama-3-1/",
      features: ["Open-Source", "GPT-4 Level", "On-Premises"],
    },
    {
      name: "Mistral Large 2",
      description: "Leading European model known for its efficiency and strong performance.",
      link: "https://mistral.ai/news/mistral-large-2/",
      features: ["Open-Source", "Efficient", "European Data"],
    },
    {
      name: "Mixtral 8x22B",
      description: "A sparse mixture-of-experts model, excellent for complex tasks and speed.",
      link: "https://mistral.ai/news/mixtral-of-experts/",
      features: ["MoE", "Fast", "Versatile"],
    },
    {
      name: "BioMistral",
      description: "Specialized for biomedical applications, trained on extensive medical literature.",
      link: "https://mistral.ai/news/biomistral/",
      features: ["Medical AI", "Domain Specific", "Accurate"],
    },
  ];

  const agentsList = [
    {
      name: "OpenDevin",
      description: "An open-source alternative to Devin, capable of autonomous software engineering.",
      link: "https://github.com/OpenDevin/OpenDevin",
      features: ["Code Generation", "Autonomous", "Open-Source"],
    },
    {
      name: "AutoGPT",
      description: "An experimental open-source attempt to make GPT-4 fully autonomous.",
      link: "https://github.com/Significant-Gravitas/AutoGPT",
      features: ["Autonomous", "Goal-Oriented", "Flexible"],
    },
    {
      name: "SuperAGI",
      description: "A developer-friendly framework to build, manage, and run autonomous AI agents.",
      link: "https://superagi.com/",
      features: ["Dev-Friendly", "Multi-Modal", "Managed"],
    },
    {
      name: "BabyAGI",
      description: "A simpler, more focused version of autonomous AI agent, designed for clarity.",
      link: "https://github.com/yoheinakajima/babyagi",
      features: ["Simple", "Focused", "Experimental"],
    },
  ];

  const handleShowModelModal = (type) => {
    if (type === "models") {
      setModelModalContent({
        heading: "Explore Available AI Models",
        models: modelsList,
        agents: [],
      });
    } else if (type === "agents") {
      setModelModalContent({
        heading: "Discover Open Source AI Agents",
        models: [],
        agents: agentsList,
      });
    }

    setShowModelModal(true);
  };

  const handleCloseModelModal = () => setShowModelModal(false);

  return (
    <div
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        padding: "0",
      }}
    >
      <Header />
      <div
        style={{
          maxWidth: "1500px",
          margin: "0 auto",
          padding: "0 24px", /* Added global horizontal padding for consistent outer margins */
        }}
      >
        <HeroSection 
          handleShowContactModal={handleShowContactModal} // Only pass this prop now
        />
        <PlatformCapabilitiesSection /> {/* New section added here */}
        <WhyLibertusSection />
        <FullWidthCTACard 
          heading="Choose Your Intelligence: Models & Agents at Your Command."
          subheading="Unleash the full power of AI with unparalleled flexibility. Select from a dynamic range of cutting-edge models and open-source agents, seamlessly integrated and ready for deployment on your premises. Adapt your AI capabilities to your precise needs, confident in complete data sovereignty and control."
          button1Text="Explore Models"
          onButton1Click={() => handleShowModelModal("models")}
          button2Text="Open Source Agents"
          onButton2Click={() => handleShowModelModal("agents")}
          animateHeading={true}
        />
        <IndustrySolutionsSection />
        <HowItWorksSection />
        <ArchitectureSection />
        <FullWidthCTACard 
          heading="Secure Your Future: Experience Sovereign AI with a 30-Day Evidence Pack Pilot."
          subheading="Eliminate the critical risks of cloud AI and achieve undeniable data sovereignty. Our exclusive 30-Day Evidence Pack Pilot allows your enterprise to rigorously test Libertus AI directly within your infrastructure, validate against your unique compliance mandates, and empower your team with absolute control. This isn’t just a pilot; it’s your definitive path to sovereign, secure, and fully compliant enterprise intelligence. Witness the proof, without commitment."
          button1Text="Start Your Sovereign AI Pilot"
          button2Text="Request Detailed Proposal & Case Studies"
          animateHeading={false}
        />
        <NewsCarouselSection />
      </div>
      <Footer />
      <ModelSelectionModal
        show={showModelModal}
        onHide={handleCloseModelModal}
        heading={modelModalContent.heading}
        modelsList={modelModalContent.models}
        agentsList={modelModalContent.agents}
      />
      <ChatbotModal />
      <ContactUsModal show={showContactModal} onHide={handleCloseContactModal} showNotification={handleShowToast} /> {/* New Contact Us Modal */}
      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={handleCloseToast} delay={3000} autohide>
          <Toast.Header>
            <strong className="me-auto">Notification</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

const Header = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    setLogoLoaded(true);
  }, []);

  return (
    <>
      {/* Top utility bar */}
      <div
        style={{
          backgroundColor: "#2a2a2a",
          padding: "5px 0",
          fontSize: "12px",
          color: "#cccccc",
        }}
      >
        <div
          style={{
            maxWidth: "1500px",
            margin: "0 auto",
            padding: "0 24px",
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div>
              On-Premise AI Infrastructure | Zero CLOUD Act Exposure |{" "}
              <span style={{ color: "#FFD700" }}>100% Data Sovereignty</span> &nbsp;
            </div>
            <div className="d-flex align-items-center" style={{ gap: "20px" }}>
              <span>🛡️ Air-Gap Capable</span>
              <span>🔐 Protected B Certified</span>
              <span>🍁 Canadian Owned</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main header - Fixed width to match content */}
      <div style={{ padding: "26px 0", backgroundColor: "#f8f9fa" }}>
        <div
          style={{
            maxWidth: "1500px",
            margin: "0 auto",
            padding: "0 24px",
          }}
        >
          <Navbar
            bg="white"
            expand="lg"
            className="navbar-card header-animation-on-load"
            style={{
              borderRadius: "24px",
              padding: "10px 18px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            }}
          >
            <Navbar.Brand
              href="#"
              className={`d-flex align-items-center navbar-brand-logo ${logoLoaded ? 'loaded' : ''}`}
              style={{ gap: "12px" }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "linear-gradient(145deg,rgba(34, 34, 34, 0.56), #000000)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: "700",
                  boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
                  transition: "transform 0.6s ease",
                }}
                onMouseEnter={(e) => {e.currentTarget.style.transform = "scale(1.2)"}}
                onMouseLeave={(e) => {e.currentTarget.style.transform = "scale(1)"}}
              >
                L
              </div>
              <span
                style={{
                  fontSize: "22px",
                  fontWeight: "600",
                  background: "linear-gradient(90deg, #1a1a1a 70%, #444444)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.2px",
                }}
              >
                Libertus.ai
              </span>
            </Navbar.Brand>

            <Navbar.Toggle aria-controls="basic-navbar-nav" />

            <Navbar.Collapse id="basic-navbar-nav" className="justify-content-end">
              <Nav style={{ gap: "16px" }}>
                <Nav.Link onClick={() => scrollToSection("platform-capabilities")}
                          style={{ fontSize: "16px", fontWeight: "500", color: "#1a1a1a" }}>
                  Platform
                </Nav.Link>
                <Nav.Link onClick={() => scrollToSection("the-risk-reality")}
                          style={{ fontSize: "16px", fontWeight: "500", color: "#1a1a1a" }}>
                  Security
                </Nav.Link>
                <Nav.Link onClick={() => scrollToSection("industry-solutions")}
                          style={{ fontSize: "16px", fontWeight: "500", color: "#1a1a1a" }}>
                  Solutions
                </Nav.Link>
                <Nav.Link onClick={() => scrollToSection("the-libertus-difference")}
                          style={{ fontSize: "16px", fontWeight: "500", color: "#1a1a1a" }}>
                  Architecture
                </Nav.Link>
                <Nav.Link onClick={() => scrollToSection("news-carousel-section")}
                          style={{ fontSize: "16px", fontWeight: "500", color: "#1a1a1a" }}>
                  Resources
                </Nav.Link>
              </Nav>
            </Navbar.Collapse>
          </Navbar>
        </div>
      </div>

      {/* Keep all existing styles */}
      <style>{`
        .navbar-nav .nav-link,
        .navbar-nav .nav-dropdown .dropdown-toggle {
          position: relative;
          padding: 8px 16px !important;
          border-radius: 20px;
          transition: all 0.3s ease;
          background-color: transparent;
        }

        .navbar-nav .nav-link:hover,
        .navbar-nav .nav-dropdown .dropdown-toggle:hover,
        .navbar-nav .nav-dropdown.show .dropdown-toggle {
          background-color: #f6f1e7 !important;
          color: #1a1a1a !important;
          transform: translateY(-1px);
        }

        .navbar-nav .nav-link:focus,
        .navbar-nav .nav-dropdown .dropdown-toggle:focus {
          background-color: transparent !important;
          outline: none;
          box-shadow: none;
        }

        .navbar-nav .nav-dropdown .dropdown-toggle::after {
          margin-left: 0.5em;
        }
        
        .dropdown-menu {
          border-radius: 16px;
          border: none;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15);
          margin-top: 8px;
        }
        
        .dropdown-item {
          border-radius: 8px;
          margin: 2px 8px;
          transition: all 0.2s ease;
        }
        
        .dropdown-item:hover,
        .dropdown-item:focus {
          background-color: #f6f1e7 !important;
          color: #1a1a1a !important;
          transform: translateY(-1px);
        }

        .nav-dropdown:hover .dropdown-menu {
          display: block !important;
        }

        .nav-dropdown .dropdown-menu {
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.3s ease;
          display: block !important;
        }

        .nav-dropdown:hover .dropdown-menu,
        .nav-dropdown.show .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        @media (max-width: 991px) {
          .navbar-nav {
            text-align: center;
            padding-top: 1rem;
          }
          
          .navbar-nav .nav-link,
          .navbar-nav .nav-dropdown .dropdown-toggle {
            margin: 4px 0;
          }
        }
      `}</style>
    </>
  );
};

// Footer Component - Updated with rounded elements and better content
const Footer = () => {
  return (
    <footer
      style={{
        backgroundColor: "#f6f1e7", /* Changed footer background to off-white/beige */
        padding: "40px 0 20px",
      }}
    >
      <div
        style={{
          maxWidth: "1500px",
          margin: "0 auto",
          padding: "0 48px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "32px",
            flexWrap: "wrap",
            gap: "40px",
          }}
        >
          <div style={{ flex: "0 0 200px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor: "#1a1a1a", /* Changed to dark for contrast */
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#f6f1e7", /* Changed to light for contrast */
                  fontSize: "16px",
                  fontWeight: "700",
                }}
              >
                L
              </div>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#1a1a1a", /* Changed to dark for contrast */
                }}
              >
                Libertus
              </span>
            </div>
            <p
              style={{
                fontSize: "14px",
                color: "#555555", /* Darker text for readability */
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              Every day with cloud AI is another day at risk. Choose sovereignty.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "60px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h5
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1a1a1a", /* Dark text for contrast */
                  marginBottom: "16px",
                  margin: "0 0 16px 0",
                }}
              >
                Quick Links
              </h5>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                }}
              >
                <li style={{ marginBottom: "8px" }}>
                  <a
                    href="#"
                    style={{
                      color: "#555555", /* Darker text for readability */
                      textDecoration: "none",
                      fontSize: "13px",
                      transition: "color 0.2s ease",
                    }}
                    onMouseOver={(e) => (e.target.style.color = "#1a1a1a")}
                    onMouseOut={(e) => (e.target.style.color = "#555555")}
                  >
                    Documentation
                  </a>
                </li>
                <li style={{ marginBottom: "8px" }}>
                  <a
                    href="#"
                    style={{
                      color: "#555555", /* Darker text for readability */
                      textDecoration: "none",
                      fontSize: "13px",
                      transition: "color 0.2s ease",
                    }}
                    onMouseOver={(e) => (e.target.style.color = "#1a1a1a")}
                    onMouseOut={(e) => (e.target.style.color = "#555555")}
                  >
                    Compliance Guides
                  </a>
                </li>
                <li style={{ marginBottom: "8px" }}>
                  <a
                    href="#"
                    style={{
                      color: "#555555", /* Darker text for readability */
                      textDecoration: "none",
                      fontSize: "13px",
                      transition: "color 0.2s ease",
                    }}
                    onMouseOver={(e) => (e.target.style.color = "#1a1a1a")}
                    onMouseOut={(e) => (e.target.style.color = "#555555")}
                  >
                    Security Architecture
                  </a>
                </li>
                <li style={{ marginBottom: "8px" }}>
                  <a
                    href="#"
                    style={{
                      color: "#555555", /* Darker text for readability */
                      textDecoration: "none",
                      fontSize: "13px",
                      transition: "color 0.2s ease",
                    }}
                    onMouseOver={(e) => (e.target.style.color = "#1a1a1a")}
                    onMouseOut={(e) => (e.target.style.color = "#555555")}
                  >
                    Canadian Privacy Laws
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h5
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1a1a1a", /* Dark text for contrast */
                  marginBottom: "16px",
                  margin: "0 0 16px 0",
                }}
              >
                Contact
              </h5>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                }}
              >
                <li style={{ marginBottom: "8px", fontSize: "13px", color: "#555555" }}>
                  📞 1-833-SOVEREIGN
                </li>
                <li style={{ marginBottom: "8px", fontSize: "13px", color: "#555555" }}>
                  ✉️ evidence@libertus.ai
                </li>
                <li style={{ marginBottom: "8px", fontSize: "13px", color: "#555555" }}>
                  📍 Canadian offices only
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid rgba(26,26,26,0.1)", /* Adjusted border color for light background */
            paddingTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <p
            style={{
              fontSize: "12px",
              color: "#555555", /* Darker text for readability */
              margin: 0,
            }}
          >
            © {new Date().getFullYear()} Libertus, Inc. All rights reserved.
          </p>
          <div
            style={{
              display: "flex",
              gap: "24px",
              fontSize: "12px",
              color: "#555555", /* Darker text for readability */
            }}
          >
            <span>🇨🇦 100% Canadian Owned</span>
            <span>✅ PHIPA Compliant</span>
            <span>✅ Law 25 Ready</span>
            <span>✅ Protected B Capable</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default App;
